import FileUpload from '@/shared/components/custom/file-upload';
import { UploadedFile } from '@/shared/types/file-upload';
import { cn } from '@/shared/utils/classname-merger';
import { Checkbox, Radio, RadioGroup, Select, SelectItem } from '@nextui-org/react';
import React, { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';

// Helper function to extract clean file name from URL
const extractFileNameFromUrl = (url: string): string => {
  if (!url || url.includes('previous_sessions')) return '';
  const urlWithoutQuery = url.split('?')[0];
  const fileName = urlWithoutQuery.substring(
    urlWithoutQuery.lastIndexOf('/') + 1
  );
  return decodeURIComponent(fileName);
};

export interface QuestionResponseFieldProps {
  index: number;
  onFocus?: () => void;
  onFileUploadError?: (hasError: boolean) => void;
  onFileUploadStatusChange?: (isUploading: boolean) => void;
  isViewMode?: boolean;
}

export const QuestionResponseField: React.FC<QuestionResponseFieldProps> = ({
  index,
  onFocus,
  onFileUploadError,
  onFileUploadStatusChange,
  isViewMode = false,
}) => {
  const { register, setValue, watch, formState: { errors } } = useFormContext();

  // Get question data from form context
  const question = watch(`questions.${index}`);
  const fieldPath = `questions.${index}.response`;

  // Get error for this question
  const questionErrors = errors?.questions as any;
  const questionError = questionErrors?.[index];
  const error = questionError?.response?.message;

  if (!question) return null;

  const {
    question: questionText,
    question_type: questionType,
    required,
    is_dropdown: isDropdown = false,
    response_choices: responseChoices = [],
    max_number_of_files: maxFiles = 5,
    question_number: questionNumber,
  } = question;

  // Watch value for controlled components (select, checkbox, radio)
  const value = watch(fieldPath);

  // Type guard to ensure value is safe to use
  const safeValue = (val: string | string[] | null | undefined): string | string[] => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return val;
    return questionType === 'multiple_choice' ? [] : '';
  };

  const currentValue = safeValue(value);

  // Individual checkbox change handler
  const handleCheckboxChange = useCallback(
    (choice: string, isSelected: boolean) => {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = isSelected
        ? [...currentValues, choice]
        : currentValues.filter((v) => v !== choice);
      setValue(fieldPath, newValues);
      onFocus?.();
    },
    [value, setValue, fieldPath, onFocus]
  );

  // Question header
  const questionHeader = (
    <div className="flex flex-row items-start p-0 gap-2 w-full">
      <span className="flex-1 text-sm leading-5 font-normal text-[#3F3F46]" style={{ fontFamily: 'Inter' }}>
        {questionText}
        {required && <span className="text-red-500 ml-1">*</span>}
      </span>
    </div>
  );

  const renderField = () => {
    switch (questionType) {
      case 'free_text': {
        const textareaRef = useRef<HTMLTextAreaElement | null>(null);
        const { ref: registerRef, ...rest } = register(fieldPath);

        // Auto-adjust height when value changes
        useEffect(() => {
          if (textareaRef.current) {
            // Reset height to measure actual content height
            textareaRef.current.style.height = '44px';
            const scrollHeight = textareaRef.current.scrollHeight;
            const newHeight = Math.max(44, scrollHeight);
            textareaRef.current.style.height = `${newHeight}px`;
          }
        }, [currentValue]);

        return (
          <textarea
            {...rest}
            ref={(e) => {
              registerRef(e);
              textareaRef.current = e;
              // Set initial height on mount
              if (e) {
                const scrollHeight = e.scrollHeight;
                e.style.height = `${Math.max(44, scrollHeight)}px`;
              }
            }}
            rows={1}
            placeholder="Add response"
            readOnly={isViewMode}
            className={cn(
              'w-full text-sm leading-5 font-normal outline-none bg-[#FAFAFA] rounded-md border resize-none overflow-hidden',
              'hover:border-[#D4D4D8] focus:border-[#3F3F46] focus:bg-white',
              'placeholder:text-[#A1A1AA]',
              error ? 'border-red-500' : 'border-[#E4E4E7]',
              isViewMode && 'cursor-not-allowed opacity-70'
            )}
            style={{
              fontFamily: 'Inter',
              boxSizing: 'border-box',
              minHeight: '44px',
              padding: '11px 12px',
              lineHeight: '20px',
            }}
            onInput={(e) => {
              if (isViewMode) return;
              const target = e.target as HTMLTextAreaElement;
              // Reset height to measure actual content height
              target.style.height = '44px';
              const scrollHeight = target.scrollHeight;
              const newHeight = Math.max(44, scrollHeight);
              target.style.height = `${newHeight}px`;
            }}
            onFocus={onFocus}
          />
        );
      }

      case 'single_choice':
        const shouldRenderAsDropdown = isDropdown || responseChoices.length > 6;

        if (!shouldRenderAsDropdown) {
          return (
            <div
              className={cn(
                'flex flex-col items-start pb-2 gap-3 w-full rounded-lg',

              )}
            >
              <RadioGroup
                value={typeof currentValue === 'string' ? currentValue : ''}
                onValueChange={(selectedValue) => {
                  if (isViewMode) return;
                  setValue(fieldPath, selectedValue);
                  onFocus?.();
                }}
                aria-label={`Select answer for ${questionText}`}
                isDisabled={isViewMode}
              >
                {responseChoices.map((choice: string, idx: number) => (
                  <Radio
                    key={idx}
                    value={choice}
                    classNames={{
                      base: 'm-0',
                      label: 'text-sm text-[#11181C]',
                    }}
                    style={{ fontFamily: 'Inter' }}
                  >
                    {choice}
                  </Radio>
                ))}
              </RadioGroup>
            </div>
          );
        } else {
          return (
            <div
              className={cn(
                'flex flex-row items-center p-3 gap-2 w-full h-11 bg-[#FAFAFA] border rounded-md border-[#E4E4E7]',
              )}
              style={{ boxSizing: 'border-box' }}
            >
              <Select
                size="sm"
                placeholder="Select an option"
                selectedKeys={
                  typeof currentValue === 'string' && responseChoices.includes(currentValue)
                    ? [currentValue]
                    : []
                }
                onSelectionChange={(keys) => {
                  if (isViewMode) return;
                  const selectedArray = Array.from(keys);
                  setValue(fieldPath, (selectedArray[0] as string) || '');
                  onFocus?.();
                }}
                isDisabled={isViewMode}
                classNames={{
                  mainWrapper: 'w-full',
                  trigger:
                    'flex items-center min-h-unit-6 h-8 border-0 shadow-none bg-transparent hover:bg-transparent data-[hover=true]:bg-transparent px-0 py-0',
                  value: 'flex items-center text-sm font-normal',
                  popoverContent: 'shadow-none border-1 border-gray-300',
                }}
              >
                {responseChoices.map((choice: string) => (
                  <SelectItem key={choice} value={choice}>
                    {choice}
                  </SelectItem>
                ))}
              </Select>
            </div>
          );
        }

      case 'multiple_choice':
        const isSmallMultiList = responseChoices.length <= 8;

        if (isSmallMultiList) {
          return (
            <div
              className={cn(
                'flex flex-col items-start pb-2 gap-3 w-full rounded-lg',
              )}
            >
              <div className="flex flex-col gap-3">
                {responseChoices.map((choice: string, idx: number) => {
                  const isChecked = Array.isArray(currentValue) && currentValue.includes(choice);
                  return (
                    <Checkbox
                      key={idx}
                      isSelected={isChecked}
                      classNames={{
                        base: 'm-0',
                        label: 'text-sm text-[#11181C]',
                      }}
                      style={{ fontFamily: 'Inter' }}
                      onChange={(e) => {
                        if (isViewMode) return;
                        handleCheckboxChange(choice, e.target.checked);
                      }}
                      isDisabled={isViewMode}
                    >
                      {choice}
                    </Checkbox>
                  );
                })}
              </div>
            </div>
          );
        } else {
          return (
            <div
              className={cn(
                'flex flex-row items-start p-3 gap-2 w-full h-11 bg-[#FAFAFA] border rounded-md',
                error ? 'border-red-500' : 'border-[#E4E4E7]'
              )}
              style={{ boxSizing: 'border-box' }}
            >
              <Select
                placeholder="Select"
                selectionMode="multiple"
                selectedKeys={
                  Array.isArray(currentValue)
                    ? currentValue.filter((v) => responseChoices.includes(v))
                    : []
                }
                onSelectionChange={(keys) => {
                  if (isViewMode) return;
                  const selectedArray = Array.from(keys) as string[];
                  setValue(fieldPath, selectedArray);
                  onFocus?.();
                }}
                variant="flat"
                aria-label={`Select answers for ${questionText}`}
                isDisabled={isViewMode}
                classNames={{
                  base: 'flex-1',
                  trigger: 'shadow-none border-none bg-transparent p-0 min-h-0 h-5',
                  value: cn(
                    'text-sm leading-5 font-normal',
                    Array.isArray(currentValue) && currentValue.length > 0
                      ? 'text-[#11181C]'
                      : 'text-[#A1A1AA]'
                  ),
                  selectorIcon: 'w-4 h-4 text-[#71717A]',
                }}
                style={{ fontFamily: 'Inter' }}
              >
                {responseChoices.map((choice: string) => (
                  <SelectItem key={choice} value={choice}>
                    {choice}
                  </SelectItem>
                ))}
              </Select>
            </div>
          );
        }

      case 'file_upload': {
        // For file_upload, load existing files from source_urls (not response)
        // Also check response array as a fallback for when source_urls isn't populated yet
        const existingUrls = question.source_urls && question.source_urls.length > 0
          ? question.source_urls
          : (Array.isArray(question.response) ? question.response : []);

        const initialFiles: UploadedFile[] = Array.isArray(existingUrls) && existingUrls.length > 0
          ? existingUrls.map((url: string) => {
            // Use extractFileNameFromUrl for clean file names
            const fileName = extractFileNameFromUrl(url) || 'file';
            return {
              id: url,
              file: new File([], fileName, { type: 'application/octet-stream' }),
              preview: url,
              status: 'success' as const,
              progressBar: false,
              signedUrl: url,
            };
          })
          : [];

        const handleSetFiles = (files: UploadedFile[]) => {
          // Extract signed URLs from successfully uploaded files
          const signedUrls = files
            .filter((f) => f.status === 'success' && f.signedUrl)
            .map((f) => f.signedUrl as string);

          setValue(fieldPath, signedUrls);
          onFocus?.();
        };

        // Get allowed file types - default to common document types
        const allowedFileTypes = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'image/jpeg',
          'image/png',
          'text/csv',
        ];

        return (
          <div className='w-full'>
            <FileUpload
              isMultiple={true}
              maxFiles={maxFiles}
              allowedFileTypes={allowedFileTypes}
              setFiles={handleSetFiles}
              initialFiles={initialFiles}
              maxSizeMB={20}
              className='min-w-[500px] min-h-[180px]'
              orientation='horizontal'
              onErrorStatusChange={onFileUploadError}
              onUploadStatusChange={onFileUploadStatusChange}
              isDisabled={isViewMode}
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col items-start p-0 gap-1.5 w-full">
      {questionHeader}
      {renderField()}
      {error && (
        <span className="text-xs text-red-500 font-normal" style={{ fontFamily: 'Inter' }}>
          {error}
        </span>
      )}
    </div>
  );
};

export default QuestionResponseField;
